import { Link } from "react-router-dom";
import { AlertTriangle, Lightbulb, ArrowRight } from "lucide-react";
import type { CommandCentreResponse } from "@/api/intelligence";
import { IntelligenceSeverityBadge } from "@/components/intelligence/IntelligenceSeverityBadge";
import { Card, CardContent } from "@/components/ui/card";
import { formatInr } from "@/lib/intelligenceFormat";

type Props = {
  data: CommandCentreResponse;
  basePath?: string;
};

export function CommandCentreFindingsSection({ data, basePath = "/hospital-admin" }: Props) {
  const findings = data.findings;
  if (!findings) return null;

  const leakagePath = `${basePath}/intelligence/leakage`;
  const insightsPath = `${basePath}/intelligence/insights`;

  return (
    <>
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Potential Revenue Leakage
          </h2>
          <Link to={leakagePath} className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Open cases</p>
              <p className="text-2xl font-semibold">{findings.leakage.openCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Critical</p>
              <p className="text-2xl font-semibold text-red-700">{findings.leakage.criticalCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Top case amount</p>
              <p className="text-2xl font-semibold">{formatInr(findings.leakage.topLeakageAmount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Total potential</p>
              <p className="text-2xl font-semibold">{formatInr(findings.leakage.totalPotentialAmount)}</p>
            </CardContent>
          </Card>
        </div>
        {findings.leakage.topCases.length > 0 ? (
          <div className="space-y-2">
            {findings.leakage.topCases.map((c) => (
              <Link key={c.id} to={`${leakagePath}?case=${c.id}`}>
                <Card className="hover:bg-muted/40 transition-colors">
                  <CardContent className="py-3 flex flex-wrap items-center gap-2 text-sm">
                    <IntelligenceSeverityBadge severity={c.severity} />
                    <span className="font-medium flex-1 min-w-[200px]">{c.title}</span>
                    <span className="text-muted-foreground">{c.hospitalName}</span>
                    <span className="font-medium">{formatInr(c.amount ?? 0)}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-4 text-sm text-muted-foreground">
              No open leakage cases. Run detection via scheduled aggregation or contact admin.
            </CardContent>
          </Card>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-indigo-600" />
            Key Insights
          </h2>
          <Link to={insightsPath} className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {findings.insights.topInsights.length > 0 ? (
          <div className="space-y-2">
            {findings.insights.topInsights.map((ins) => (
              <Link key={ins.id} to={`${insightsPath}?insight=${ins.id}`}>
                <Card className="hover:bg-muted/40 transition-colors">
                  <CardContent className="py-3 space-y-1 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <IntelligenceSeverityBadge severity={ins.severity} />
                      {ins.insightType && (
                        <span className="text-xs rounded bg-slate-100 px-2 py-0.5">{ins.insightType}</span>
                      )}
                      <span className="font-medium">{ins.title}</span>
                    </div>
                    <p className="text-muted-foreground line-clamp-2">{ins.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-4 text-sm text-muted-foreground">No active insights for this scope.</CardContent>
          </Card>
        )}
      </section>
    </>
  );
}
